'use client';

import React, { memo, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import {
  PerformanceLabPlan,
  PlanIncludeItem,
  PlanIncludeDetail,
} from './types';
import { useModal } from 'components/shared/Modal';
import { usePerformanceLabsIndia } from './PerformanceLabsIndiaContext';
import IncludeDetailSidebar from './IncludeDetailSidebar';
import { CartAdd, CartDecrease } from 'assets/icons';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { DotSpinner } from 'components/shared';
import {
  resolvePerformanceLabBuyCategory,
} from 'components/performance-lab/utils';
import { useHandlePerformanceLabBuyNavigate } from 'components/performance-lab/hooks';

import { findCartLine } from './performanceLabCartUtils';
import { getActivePlanOffer } from './planOffer';
import styles from './PlanCard.module.css';

interface PlanCardProps {
  plan: PerformanceLabPlan;
  variant?: 'default' | 'compact' | 'service';
  sectionId?: string;
  /** @deprecated No longer used — thumbnails are resolved from PlanIncludeDetail.thumbnailUrl */
  compareData?: unknown[];
}

const ChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.59961 12L9.59961 8L5.59961 4"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.375 10.7188L12.3594 17.2813L9.625 14.2983"
      stroke="#121212"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_2567_156)">
      <path
        d="M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11Z"
        stroke="#1539F5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 8V6"
        stroke="#1539F5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 4H6.00667"
        stroke="#1539F5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_2567_156">
        <rect width="12" height="12" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const ServicePrice: React.FC<
  React.HTMLAttributes<HTMLSpanElement> & { $compact?: boolean }
> = ({ $compact, className, children, ...rest }) => (
  <span
    className={`${styles.servicePrice}${className ? ` ${className}` : ''}`}
    data-compact={$compact ? 'true' : undefined}
    {...rest}
  >
    {children}
  </span>
);

const OriginalPrice: React.FC<
  React.HTMLAttributes<HTMLSpanElement> & {
    $compact?: boolean;
    $fixed?: boolean;
  }
> = ({ $compact, $fixed, className, children, ...rest }) => (
  <span
    className={`${styles.servicePrice} ${styles.originalPrice}${
      className ? ` ${className}` : ''
    }`}
    data-compact={$compact ? 'true' : undefined}
    data-fixed={$fixed ? 'true' : undefined}
    {...rest}
  >
    {children}
  </span>
);

const getLabel = (item: string | PlanIncludeItem): string =>
  typeof item === 'string' ? item : item.label;

const getDetail = (
  item: string | PlanIncludeItem,
): PlanIncludeDetail | undefined =>
  typeof item === 'string' ? undefined : item.detail;

const PlanCardComponent: React.FC<PlanCardProps> = ({
  plan,
  variant = 'default',
  sectionId,
}) => {
  const t = useTranslations('diagnostics');
  const tLab = useTranslations('performance-lab');
  const modal = useModal();
  const handleBuyNavigate = useHandlePerformanceLabBuyNavigate();
  const internationalBookLabel = tLab('cta.buyNow');
  const {
    addToCart,
    cart: cartData,
    updateCartQuantity,
    deleteFromCart,
    cartLoading,
    getPlanDiscount,
    getDiscountedPrice,
    isCartEnabled,
  } = usePerformanceLabsIndia();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [pendingQty, setPendingQty] = useState<
    'increment' | 'decrement' | null
  >(null);
  const [openSidebarInfo, setOpenSidebarInfo] = useState<{
    label: string;
    detail: PlanIncludeDetail;
    iconUrl?: string;
    sectionId?: string;
    shopifyVariantId?: string;
  } | null>(null);

  const searchParams = useSearchParams();

  const handleBookPlan = () => {
    const rawCategory = searchParams?.get('category') ?? undefined;
    const category = resolvePerformanceLabBuyCategory(rawCategory, sectionId);

    handleBuyNavigate({ planId: plan.id, category });
  };

  const isOnRequest = plan.price === 0;
  const isSoldOut = !plan.shopifyVariantId && !isOnRequest;
  // Per plan, not per page: an affiliate code can be scoped to a subset of the
  // Performance Labs catalogue, so a card only shows a discount when this
  // product is in scope.
  const affiliateDiscount = getPlanDiscount(plan.shopifyVariantId);
  const discountedPrice = getDiscountedPrice(plan.price, plan.shopifyVariantId);
  const activeOffer = !isOnRequest ? getActivePlanOffer(plan) : null;
  const hasDiscount =
    !activeOffer && affiliateDiscount.percentage > 0 && !isOnRequest;
  const cartLine = findCartLine(cartData, plan.shopifyVariantId);
  const quantityInCart = cartLine?.quantity ?? 0;

  const handleAddToCart = async () => {
    if (!plan.shopifyVariantId || adding || isOnRequest) return;
    setAdding(true);
    try {
      await addToCart(plan.shopifyVariantId, activeOffer);
    } finally {
      setAdding(false);
    }
  };

  const handleIncrement = async () => {
    setPendingQty('increment');
    try {
      if (cartLine) {
        await updateCartQuantity(
          cartLine.id,
          quantityInCart + 1,
          cartLine.attributes,
        );
      } else {
        await handleAddToCart();
      }
    } finally {
      setPendingQty(null);
    }
  };

  const handleDecrement = async () => {
    if (!cartLine) return;
    setPendingQty('decrement');
    try {
      if (quantityInCart <= 1) {
        await deleteFromCart(cartLine.id);
      } else {
        await updateCartQuantity(
          cartLine.id,
          quantityInCart - 1,
          cartLine.attributes,
        );
      }
    } finally {
      setPendingQty(null);
    }
  };

  const openDetailSidebar = (
    label: string,
    detail: PlanIncludeDetail,
    iconUrl?: string,
  ) => {
    setOpenSidebarInfo({
      label,
      detail,
      iconUrl,
      sectionId,
      shopifyVariantId: plan.shopifyVariantId,
    });
    modal.setFullView();
    modal.setCloseBtnHidden(true);
    modal.setTop();
    modal.setAlignRight();
    modal.show();
  };

  useEffect(() => {
    if (!openSidebarInfo) return;

    const handleClose = () => {
      modal.hide();
      modal.clear();
      setOpenSidebarInfo(null);
    };

    const handleSidebarAdd = async () => {
      if (plan.comingSoon) return;
      if (!plan.shopifyVariantId) return;
      await addToCart(plan.shopifyVariantId, activeOffer);
    };

    const handleSidebarIncrement = async () => {
      if (plan.comingSoon) return;
      if (cartLine) {
        await updateCartQuantity(
          cartLine.id,
          quantityInCart + 1,
          cartLine.attributes,
        );
      } else {
        if (!plan.shopifyVariantId) return;
        await addToCart(plan.shopifyVariantId, activeOffer);
      }
    };

    const handleSidebarDecrement = async () => {
      if (!cartLine) return;
      if (quantityInCart <= 1) {
        await deleteFromCart(cartLine.id);
      } else {
        await updateCartQuantity(
          cartLine.id,
          quantityInCart - 1,
          cartLine.attributes,
        );
      }
    };

    modal.set(
      <IncludeDetailSidebar
        label={openSidebarInfo.label}
        detail={openSidebarInfo.detail}
        iconUrl={openSidebarInfo.iconUrl}
        onClose={handleClose}
        onAdd={handleSidebarAdd}
        cartLine={cartLine}
        quantityInCart={quantityInCart}
        onIncrement={handleSidebarIncrement}
        onDecrement={handleSidebarDecrement}
        cartLoading={cartLoading}
        isComingSoon={!!plan.comingSoon}
        isCartEnabled={isCartEnabled}
        planId={plan.id}
        sectionId={openSidebarInfo.sectionId}
        shopifyVariantId={openSidebarInfo.shopifyVariantId}
      />,
    );
  }, [openSidebarInfo, cartLine, quantityInCart, cartLoading]);

  const hasIncludes = plan.includes && plan.includes.length > 0;

  const renderChecklistItem = (
    item: string | PlanIncludeItem,
    key: string | number,
  ) => {
    const label = getLabel(item);
    const detail = getDetail(item);
    const handleClick = () => {
      if (!detail) return;
      openDetailSidebar(
        label,
        detail,
        detail.thumbnailUrl ?? detail.imageUrl ?? plan.thumbnailUrl,
      );
    };
    return (
      <li
        key={key}
        className={`${styles.checklistItem} gtm`}
        data-buttontype="performance_lab_buy_include_section"
        data-include-label={t(label)}
        onClick={handleClick}
        data-clickable={detail ? 'true' : undefined}
      >
        <span className={styles.checkIconWrapper}>
          <CheckIcon />
        </span>
        <span className={styles.checklistLabel}>{t(label)}</span>
        {detail && <ChevronRight />}
      </li>
    );
  };

  if (variant === 'service') {
    return (
      <div className={styles.serviceCardContainer}>
        <div className={styles.defaultHeaderRow}>
          {plan.thumbnailUrl ? (
            <CustomImage
              className={styles.thumbnail}
              src={plan.thumbnailUrl}
              alt={t(plan.name)}
              width={72}
              height={72}
            />
          ) : (
            <div className={styles.thumbnailPlaceholder} />
          )}
          <div className={styles.serviceNameBlock}>
            <h3
              className={styles.serviceName}
              data-preserve-case={plan.preserveCase ? 'true' : undefined}
            >
              {t(plan.name)}
            </h3>
            <p className={styles.serviceDescription}>{t(plan.description)}</p>
          </div>
        </div>

        {plan.bulletPoints && plan.bulletPoints.length > 0 && (
          <ul className={styles.bulletList}>
            {plan.bulletPoints.map((item, idx) => (
              <li className={styles.bulletItem} key={idx}>
                {t(item)}
              </li>
            ))}
          </ul>
        )}

        <div className={styles.serviceSeparator} style={{ marginTop: 'auto' }} />

        <div className={styles.inlinePriceRow}>
          <div className={styles.priceRow}>
            {activeOffer ? (
              <>
                <ServicePrice>
                  ₹{activeOffer.offerPrice.toLocaleString('en-IN')}
                </ServicePrice>
                <OriginalPrice>
                  ₹{plan.price.toLocaleString('en-IN')}
                </OriginalPrice>
                {activeOffer.badgeLabel && (
                  <span className={styles.discountBadge}>
                    {activeOffer.badgeLabel}
                  </span>
                )}
              </>
            ) : hasDiscount ? (
              <>
                <ServicePrice>
                  ₹{discountedPrice.toLocaleString('en-IN')}
                </ServicePrice>
                <OriginalPrice>
                  ₹{plan.price.toLocaleString('en-IN')}
                </OriginalPrice>
                <span className={styles.discountBadge}>
                  {affiliateDiscount.label}
                </span>
              </>
            ) : (
              <ServicePrice>
                {isOnRequest
                  ? t('ui.onRequest')
                  : `₹${plan.price.toLocaleString('en-IN')}`}
              </ServicePrice>
            )}
          </div>
          {!isOnRequest &&
            (isCartEnabled ? (
              cartLine ? (
                <div
                  className={styles.quantityControlsWrapper}
                  data-loading={pendingQty ? 'true' : undefined}
                >
                  {pendingQty ? (
                    <DotSpinner color="rgba(0, 0, 0, 0.4)" />
                  ) : (
                    <>
                      <button
                        className={`${styles.quantityButton} gtm`}
                        data-buttontype="performance_lab_buy_quantity_decrement"
                        data-testid={`pl-qty-decrement-${plan.id}`}
                        data-product={sectionId}
                        data-variantid={plan.shopifyVariantId}
                        onClick={handleDecrement}
                        disabled={cartLoading}
                      >
                        <CartDecrease fill="#1539f5" />
                      </button>
                      <span className={styles.quantityValue}>
                        {quantityInCart}
                      </span>
                      <button
                        className={`${styles.quantityButton} gtm`}
                        data-buttontype="performance_lab_buy_quantity_increment"
                        data-testid={`pl-qty-increment-${plan.id}`}
                        data-product={sectionId}
                        data-variantid={plan.shopifyVariantId}
                        onClick={handleIncrement}
                        disabled={cartLoading}
                      >
                        <CartAdd fill="#1539f5" />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <button
                  className={`${styles.serviceAddButton} gtm`}
                  data-buttontype="performance_lab_buy_add_to_cart"
                  data-testid={`pl-add-to-cart-${plan.id}`}
                  data-product={sectionId}
                  data-variantid={plan.shopifyVariantId}
                  data-filled={plan.buttonVariant === 'filled' ? 'true' : undefined}
                  onClick={handleAddToCart}
                  disabled={adding}
                >
                  {adding ? (
                    <DotSpinner color="rgba(0, 0, 0, 0.4)" />
                  ) : (
                    t(plan.buttonLabel)
                  )}
                </button>
              )
            ) : (
              <button
                className={`${styles.serviceAddButton} gtm`}
                data-buttontype="performance_lab_buy_book"
                data-testid={`pl-book-${plan.id}`}
                data-product={sectionId}
                onClick={handleBookPlan}
              >
                {internationalBookLabel}
              </button>
            ))}
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    const handleCompactLearnMore = () => {
      if (plan.learnMore && plan.detail) {
        openDetailSidebar(plan.name, plan.detail, plan.thumbnailUrl);
      }
    };

    const isCompactClickable = !!(plan.learnMore && plan.detail);

    return (
      <div className={styles.serviceCardContainer} data-transparent="true">
        <div
          className={styles.compactHeaderRow}
          data-clickable={isCompactClickable ? 'true' : undefined}
          onClick={handleCompactLearnMore}
        >
          <div className={styles.defaultHeaderRow}>
            {plan.thumbnailUrl ? (
              <CustomImage
                className={styles.thumbnail}
                src={plan.thumbnailUrl}
                alt={t(plan.name)}
                width={72}
                height={72}
              />
            ) : (
              <div className={styles.thumbnailPlaceholder} />
            )}
            <div className={styles.serviceNameBlock}>
              <h3
                className={styles.serviceName}
                data-preserve-case={plan.preserveCase ? 'true' : undefined}
              >
                {t(plan.name)}
              </h3>
              <p className={styles.serviceDescription} data-compact="true">
                {t(plan.description)}
              </p>
            </div>
          </div>
          {plan.learnMore && plan.detail && (
            <button
              className={`${styles.learnMoreChevron} gtm`}
              data-buttontype="performance_lab_buy_learn_more"
              data-testid={`pl-learn-more-${plan.id}`}
              data-product={sectionId}
              onClick={() =>
                openDetailSidebar(
                  plan.name,
                  plan.detail as PlanIncludeDetail,
                  plan.thumbnailUrl,
                )
              }
            >
              <ChevronRight />
            </button>
          )}
        </div>

        {plan.bulletPoints && plan.bulletPoints.length > 0 && (
          <ul
            className={styles.bulletList}
            onClick={handleCompactLearnMore}
            style={isCompactClickable ? { cursor: 'pointer' } : undefined}
          >
            {plan.bulletPoints.map((item, idx) => (
              <li className={styles.bulletItem} key={idx}>
                {t(item)}
              </li>
            ))}
          </ul>
        )}

        <div className={styles.serviceSeparator} style={{ marginTop: 'auto' }} />

        <div className={styles.inlinePriceRow}>
          {isOnRequest ? (
            <span className={styles.onRequestLabel}>
              {t('ui.priceOnRequest')}
            </span>
          ) : (
            <>
              <div className={styles.priceRow}>
                {activeOffer ? (
                  <>
                    <ServicePrice $compact>
                      ₹{activeOffer.offerPrice.toLocaleString('en-IN')}
                    </ServicePrice>
                    <OriginalPrice $compact>
                      ₹{plan.price.toLocaleString('en-IN')}
                    </OriginalPrice>
                    {activeOffer.badgeLabel && (
                      <span className={styles.discountBadge}>
                        {activeOffer.badgeLabel}
                      </span>
                    )}
                  </>
                ) : hasDiscount ? (
                  <>
                    <ServicePrice $compact>
                      ₹{discountedPrice.toLocaleString('en-IN')}
                    </ServicePrice>
                    <OriginalPrice $compact>
                      ₹{plan.price.toLocaleString('en-IN')}
                    </OriginalPrice>
                    <span className={styles.discountBadge}>
                      {affiliateDiscount.label}
                    </span>
                  </>
                ) : (
                  <ServicePrice $compact>
                    {`₹${plan.price.toLocaleString('en-IN')}`}
                  </ServicePrice>
                )}
              </div>
              {isCartEnabled ? (
                cartLine ? (
                  <div
                    className={styles.quantityControlsWrapper}
                    data-loading={pendingQty ? 'true' : undefined}
                  >
                    {pendingQty ? (
                      <DotSpinner color="rgba(0, 0, 0, 0.4)" />
                    ) : (
                      <>
                        <button
                          className={`${styles.quantityButton} gtm`}
                          data-buttontype="performance_lab_buy_quantity_decrement"
                          data-testid={`pl-qty-decrement-${plan.id}`}
                          data-product={sectionId}
                          data-variantid={plan.shopifyVariantId}
                          onClick={handleDecrement}
                          disabled={cartLoading}
                        >
                          <CartDecrease fill="#1539f5" />
                        </button>
                        <span className={styles.quantityValue}>
                          {quantityInCart}
                        </span>
                        <button
                          className={`${styles.quantityButton} gtm`}
                          data-buttontype="performance_lab_buy_quantity_increment"
                          data-testid={`pl-qty-increment-${plan.id}`}
                          data-product={sectionId}
                          data-variantid={plan.shopifyVariantId}
                          onClick={handleIncrement}
                          disabled={cartLoading}
                        >
                          <CartAdd fill="#1539f5" />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <button
                    className={`${styles.serviceAddButton} gtm`}
                    data-buttontype="performance_lab_buy_add_to_cart"
                    data-testid={`pl-add-to-cart-${plan.id}`}
                    data-product={sectionId}
                    data-variantid={plan.shopifyVariantId}
                    data-filled={
                      !isSoldOut && plan.buttonVariant === 'filled'
                        ? 'true'
                        : undefined
                    }
                    onClick={handleAddToCart}
                    disabled={adding || isSoldOut}
                  >
                    {adding ? (
                      <DotSpinner color="rgba(0, 0, 0, 0.4)" />
                    ) : (
                      t(plan.buttonLabel)
                    )}
                  </button>
                )
              ) : (
                <button
                  className={`${styles.serviceAddButton} gtm`}
                  data-buttontype="performance_lab_buy_book"
                  data-testid={`pl-book-${plan.id}`}
                  data-product={sectionId}
                  onClick={handleBookPlan}
                >
                  {internationalBookLabel}
                </button>
              )}
            </>
          )}
        </div>

        {(plan.sessionInfo || plan.validity) && (
          <div className={styles.metaRow}>
            {plan.sessionInfo && (
              <span className={styles.sessionInfo}>{t(plan.sessionInfo)}</span>
            )}
            {plan.sessionInfo && plan.validity && (
              <span className={styles.metaDot} />
            )}
            {plan.validity && (
              <span className={styles.validityBadge}>{t(plan.validity)}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.cardContainer}>
      <div className={styles.defaultHeaderRow}>
        <div className={styles.headerRow}>
          {plan.thumbnailUrl ? (
            <CustomImage
              className={styles.thumbnail}
              src={plan.thumbnailUrl}
              alt={t(plan.name)}
              width={72}
              height={72}
            />
          ) : (
            <div className={styles.thumbnailPlaceholder} />
          )}
          {/* {plan.tag && <Tag>{t(plan.tag)}</Tag>} */}
        </div>
        <div className={styles.nameDescBlock}>
          <h3
            className={styles.planName}
            data-preserve-case={plan.preserveCase ? 'true' : undefined}
          >
            {t(plan.name)}
          </h3>
          {/* Session + Validity */}
          {(plan.sessionInfo || plan.validity) && (
            <div className={styles.metaRow}>
              {plan.sessionInfo && (
                <span className={styles.sessionInfo}>
                  {t(plan.sessionInfo)}
                </span>
              )}
              {plan.sessionInfo && plan.validity && (
                <span className={styles.metaDot} />
              )}
              {plan.validity && (
                <span className={styles.validityBadge}>{t(plan.validity)}</span>
              )}
            </div>
          )}
          <div className={styles.descRecoveryBlock}>
            <p className={styles.planDescription}>{t(plan.description)}</p>
            {/* Recovery note - desktop only */}
            {plan.recoveryNote && (
              <div className={styles.recoveryNote}>
                <>{t(plan.recoveryNote)}</>
                <InfoIcon />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bullet points (IV Therapy) */}
      {plan.bulletPoints && plan.bulletPoints.length > 0 && (
        <ul className={styles.bulletList}>
          {plan.bulletPoints.map((item, idx) => (
            <li className={styles.bulletItem} key={idx}>
              {t(item)}
            </li>
          ))}
        </ul>
      )}

      <div className={styles.priceButtonWrapper}>
        <div className={styles.priceRow}>
          {activeOffer ? (
            <>
              <span className={styles.price}>
                ₹{activeOffer.offerPrice.toLocaleString('en-IN')}
              </span>
              <OriginalPrice $fixed>
                ₹{plan.price.toLocaleString('en-IN')}
              </OriginalPrice>
              {activeOffer.badgeLabel && (
                <span className={styles.discountBadge}>
                  {activeOffer.badgeLabel}
                </span>
              )}
            </>
          ) : hasDiscount ? (
            <>
              <span className={styles.price}>
                ₹{discountedPrice.toLocaleString('en-IN')}
              </span>
              <OriginalPrice $fixed>
                ₹{plan.price.toLocaleString('en-IN')}
              </OriginalPrice>
              <span className={styles.discountBadge}>
                {affiliateDiscount.label}
              </span>
            </>
          ) : (
            <span className={styles.price}>
              {isOnRequest
                ? t('ui.onRequest')
                : `₹${plan.price.toLocaleString('en-IN')}`}
            </span>
          )}
        </div>
        {!isOnRequest &&
          (isCartEnabled ? (
            cartLine ? (
              <div
                className={styles.quantityControlsWrapper}
                data-full-width="true"
                data-loading={pendingQty ? 'true' : undefined}
              >
                {pendingQty ? (
                  <DotSpinner color="rgba(0, 0, 0, 0.4)" />
                ) : (
                  <>
                    <button
                      className={`${styles.quantityButton} gtm`}
                      data-buttontype="performance_lab_buy_quantity_decrement"
                      data-testid={`pl-qty-decrement-${plan.id}`}
                      data-product={sectionId}
                      data-variantid={plan.shopifyVariantId}
                      onClick={handleDecrement}
                      disabled={cartLoading}
                    >
                      <CartDecrease fill="#1539f5" />
                    </button>
                    <span className={styles.quantityValue}>
                      {quantityInCart}
                    </span>
                    <button
                      className={`${styles.quantityButton} gtm`}
                      data-buttontype="performance_lab_buy_quantity_increment"
                      data-testid={`pl-qty-increment-${plan.id}`}
                      data-product={sectionId}
                      data-variantid={plan.shopifyVariantId}
                      onClick={handleIncrement}
                      disabled={cartLoading}
                    >
                      <CartAdd fill="#1539f5" />
                    </button>
                  </>
                )}
              </div>
            ) : (
              <button
                className={`${styles.serviceAddButton} gtm`}
                data-buttontype="performance_lab_buy_add_to_cart"
                data-testid={`pl-add-to-cart-${plan.id}`}
                data-product={sectionId}
                data-variantid={plan.shopifyVariantId}
                data-filled={plan.buttonVariant === 'filled' ? 'true' : undefined}
                data-full-width="true"
                onClick={handleAddToCart}
                disabled={adding}
              >
                {adding ? (
                  <DotSpinner color="rgba(0, 0, 0, 0.4)" />
                ) : (
                  t(plan.buttonLabel)
                )}
              </button>
            )
          ) : (
            <button
              className={`${styles.serviceAddButton} gtm`}
              data-buttontype="performance_lab_buy_book"
              data-testid={`pl-book-${plan.id}`}
              data-product={sectionId}
              data-full-width="true"
              onClick={handleBookPlan}
            >
              {internationalBookLabel}
            </button>
          ))}
      </div>

      {/* Includes section with details toggle */}
      {hasIncludes && (
        <div
          className={styles.includesWrapper}
          data-mobile-visible={isDetailsOpen ? 'true' : undefined}
        >
          <div className={styles.includesInner}>
            {plan.includesHeader && (
              <span className={styles.includesHeader}>
                {t(plan.includesHeader)}
              </span>
            )}
            <ul className={styles.checklistContainer}>
              {(plan.includes ?? []).map((item, idx) =>
                renderChecklistItem(item, idx),
              )}
            </ul>
            {plan.secondaryIncludes && plan.secondaryIncludes.length > 0 && (
              <>
                {plan.secondaryIncludesHeader && (
                  <span className={styles.includesHeader}>
                    {t(plan.secondaryIncludesHeader)}
                  </span>
                )}
                <ul className={styles.checklistContainer}>
                  {plan.secondaryIncludes.map((item, idx) =>
                    renderChecklistItem(item, `secondary-${idx}`),
                  )}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      {/* Details toggle - mobile only, always visible below price/button */}
      {hasIncludes && (
        <button
          className={`${styles.detailsToggle} gtm`}
          data-buttontype="performance_lab_buy_details_toggle"
          data-testid={`pl-details-${plan.id}`}
          data-product={sectionId}
          data-open={isDetailsOpen ? 'true' : undefined}
          onClick={() => setIsDetailsOpen(!isDetailsOpen)}
        >
          {t('ui.details')}
          <svg
            className={styles.chevronDown}
            data-open={isDetailsOpen ? 'true' : undefined}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="#000000"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

const PlanCard = memo(PlanCardComponent);

PlanCard.displayName = 'PlanCard';

export default PlanCard;
